/**
 * PHARMAKON — the shell's host of a user picked by PIN on a team workstation (X XWP-72; CP §15.3
 * bullets 4-6, XD-222, XD-295, XD-296). Mounted once in the AppShell; inert on a password session.
 *
 *  · follows the picked user's activity (lib/session-equipe.js `demarrerSuiviSelection`): their own JWT
 *    is prolonged through `POST /auth/equipe/selection/prolonger` while they are active (never
 *    `/auth/refresh`), the picker comes back just before the idle deadline, and a window follows another
 *    window's « Changer d'utilisateur »;
 *  · the owner's step-up (XD-296): when a request is refused 403 PREUVE_SESSION_EQUIPE_REQUISE the API
 *    client waits here — the owner re-types HIS OWN password (`POST /auth/equipe/preuve`), the waiting
 *    requests replay; « Annuler » hands the server's refusal back to the screen that asked.
 */
import * as React from "react";
import { Alert } from "@/components/ui/Alert";
import { Button, Field, Input, Modal } from "@/components/ui/primitives";
import { useAuthStore } from "@/stores/authStore.js";
import { equipeApi } from "@/services/session-equipe.api.js";
import { MESSAGES_SESSION_EQUIPE as M, SESSION_EQUIPE_WEB as W } from "@/config/session-equipe.config.js";
import { conclurePreuve, demarrerSuiviSelection, estSelectionEquipe, usePreuveEquipe } from "@/lib/session-equipe.js";
import { erreursChamps } from "@/lib/equipe.js";

function PreuveDialog() {
  const [mdp, setMdp] = React.useState("");
  const [refus, setRefus] = React.useState(null);
  const [envoi, setEnvoi] = React.useState(false);
  const premier = React.useRef(null);
  const confirmer = async () => {
    if (envoi || !mdp) return;
    setEnvoi(true);
    setRefus(null);
    try {
      await equipeApi.preuve(mdp);
      conclurePreuve(true);
    } catch (e) {
      setRefus(erreursChamps(e).mot_de_passe ?? (typeof e?.message === "string" && e.message ? e.message : M.erreurReseau));
      setMdp("");
      premier.current?.focus();
    } finally {
      setEnvoi(false);
    }
  };
  return (
    <Modal
      open
      onClose={envoi ? undefined : () => conclurePreuve(false)}
      title={M.preuve.titre}
      size="sm"
      dirty={false}
      initialFocusRef={premier}
      footer={
        <>
          <Button variant="secondary" onClick={() => conclurePreuve(false)} disabled={envoi}>
            {M.preuve.annuler}
          </Button>
          <Button variant="primary" onClick={confirmer} loading={envoi} disabled={!mdp} data-dialog-primary="">
            {M.preuve.confirmer}
          </Button>
        </>
      }
    >
      <div className="pk-postes-equipe" data-testid="preuve-session-equipe">
        <p className="pk-postes-equipe-aide">{M.preuve.message(W.PREUVE_MINUTES)}</p>
        {refus && (
          <div role="alert">
            <Alert variant="danger">{refus}</Alert>
          </div>
        )}
        <Field label={M.preuve.champ} required>
          <Input ref={premier} type="password" name="mot-de-passe-preuve" autoComplete="current-password" value={mdp} onChange={(e) => setMdp(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

export default function SuiviSessionEquipe() {
  const enregistrerJeton = useAuthStore((s) => s.enregistrerJetonEquipe);
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const preuve = usePreuveEquipe((s) => s.ouverte);
  React.useEffect(() => {
    if (!userId || !estSelectionEquipe()) return undefined;
    return demarrerSuiviSelection({ enregistrerJeton });
  }, [userId, enregistrerJeton]);
  return preuve ? <PreuveDialog /> : null;
}
